import { Fancybox } from '@fancyapps/ui/dist/fancybox/fancybox.js';
import Bouncer from 'formbouncerjs';

const ALLOWED_EXT = ['pdf', 'doc', 'docx'];
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB

function isValidFileExt(file) {
    const ext = file.name.includes('.') ? file.name.split('.').pop().toLowerCase() : '';
    return ALLOWED_EXT.includes(ext);
}

function isWithinSizeLimit(file) {
    return file.size <= MAX_FILE_SIZE;
}

function initBouncerForForm(form) {
    if (form._jobApplyBouncer)
    {
        return form._jobApplyBouncer;
    }

    const msgs = form.dataset;

    const bouncer = new Bouncer(`#${form.id}`, {
        errorClass: 'error-message',
        validClass: 'is-valid',
        fieldPrefix: 'bouncer-field-',
        errorPrefix: 'bouncer-error-',
        messages: {
            missingValue: {
                default: msgs.msgRequired,
                checkbox: msgs.msgRequired,
                radio: msgs.msgRequired,
                select: msgs.msgRequired,
            },
            patternMismatch: {
                default: msgs.msgPhone,
                email: msgs.msgEmail,
            },
            typeMismatch: {
                email: msgs.msgEmail,
                url: msgs.msgRequired,
            },
            fallback: msgs.msgError,
            customValidations: {
                invalidFileType: msgs.msgFileType,
                fileTooLarge: msgs.msgFileSize,
            },
        },
        customValidations: {
            invalidFileType(field) {
                if (field.type !== 'file' || !field.files.length) return false;
                return Array.from(field.files).some((f) => !isValidFileExt(f));
            },
            fileTooLarge(field) {
                if (field.type !== 'file' || !field.files.length) return false;
                return Array.from(field.files).some((f) => !isWithinSizeLimit(f));
            },
        },
    });

    form._jobApplyBouncer = bouncer;

    form.querySelectorAll('input[type="file"]').forEach((input) =>
    {
        input.addEventListener('change', () =>
        {
            updateFileName(input);
        });
    });

    return bouncer;
}

function updateFileName(input) {
    const nameEl = input.closest('.file-input')?.querySelector('.file-input__name');
    if (!nameEl) return;
    const files = Array.from(input.files);
    nameEl.textContent = files.length
        ? files.map((f) => f.name).join(', ')
        : (nameEl.dataset.default ?? '');
}

function clearFieldErrors(form) {
    form.querySelectorAll('[data-server-error]').forEach((el) => el.remove());

    form.querySelectorAll('[aria-invalid="true"]').forEach((el) => {
        el.removeAttribute('aria-invalid');
    });
}

function applyFieldErrors(form, errors) {
    clearFieldErrors(form);

    Object.entries(errors ?? {}).forEach(([field, message]) => {
        const input = form.querySelector(`[name="${field}"], [name="${field}[]"]`);

        if (!input) return;

        const errorId = `${input.id}-error`;
        const errorEl = document.createElement('span');

        errorEl.className = 'error-message';
        errorEl.id = errorId;
        errorEl.dataset.serverError = 'true';
        errorEl.textContent = message;

        input.setAttribute('aria-invalid', 'true');
        input.setAttribute('aria-describedby', [input.getAttribute('aria-describedby'), errorId].filter(Boolean).join(' '));
        input.closest('.form-field')?.appendChild(errorEl);
    });
}

function resetFormState(form, wrapper) {
    const formBody = wrapper.querySelector('.job-apply__form-body');
    const notification = wrapper.querySelector('.form-notification');

    form.reset();
    form.querySelectorAll('.file-input__name').forEach((el) => {
        el.textContent = el.dataset.default ?? '';
    });
    form.querySelector('.form-actions__submit-wrap')?.classList.remove('is-loading');
    clearFieldErrors(form);

    if (formBody) {
        formBody.hidden = false;
    }

    if (notification) {
        notification.hidden = true;
        notification.classList.remove('is-success', 'is-error');
    }
}

async function submitForm(form) {
    const submitWrap = form.querySelector('.form-actions__submit-wrap');

    if (submitWrap?.classList.contains('is-loading')) {
        return;
    }

    submitWrap?.classList.add('is-loading');

    try {
        const res = await fetch(form.action, {
            method: 'POST',
            headers: { 'X-Requested-With': 'XMLHttpRequest' },
            body: new FormData(form),
        });

        const json = await res.json();
        showResult(form, json.success === true, json.message, json.errors);

    } catch {
        showResult(form, false, form.dataset.msgError);
    } finally {
        submitWrap?.classList.remove('is-loading');
    }
}

function showResult(form, success, message, errors) {
    const wrapper = form.closest('[id^="job-apply-modal-"]');
    if (!wrapper) return;

    const formBody = wrapper.querySelector('.job-apply__form-body');
    const notification = wrapper.querySelector('.form-notification');
    const msgEl = wrapper.querySelector('.form-notification__message');

    if (!formBody || !notification || !msgEl)
    {
        return;
    }

    notification.hidden = false;
    notification.classList.remove('is-success', 'is-error');
    notification.classList.toggle('is-success', success);
    notification.classList.toggle('is-error', !success);
    msgEl.textContent = message ?? '';

    if (success) {
        // Only the success case clears the form from view — on failure the
        // form and the applicant's input stay visible and correctable.
        formBody.hidden = true;
    } else {
        applyFieldErrors(form, errors);

        const firstInvalid = form.querySelector('[aria-invalid="true"]');
        firstInvalid ? firstInvalid.focus() : notification.focus?.();
    }
}

function bindFormSubmission(form)
{
    if (form._jobApplySubmitBound) {
        return;
    }

    form.querySelector('button[type="submit"]').addEventListener('click', async ev =>
    {
        ev.preventDefault();

        const bouncer = form._jobApplyBouncer;

        if ( ! bouncer)
        {
            return false;
        }

        const errors = bouncer.validateAll(form);

        if (errors.length > 0)
        {
            return false;
        }

        await submitForm(form);
    });

    // form.addEventListener('submit', async (ev) =>
    // {
    //     ev.preventDefault();
    //
    //     const bouncer = form._jobApplyBouncer;
    //
    //     if (!bouncer) {
    //         return;
    //     }
    //
    //     const errors = bouncer.validateAll(form);
    //
    //     if (errors.length > 0) {
    //         return;
    //     }
    //
    //     await submitForm(form);
    // });

    form._jobApplySubmitBound = true;
}

export function initJobApply() {
    document.querySelectorAll('[data-job-apply]').forEach((trigger) =>
    {
        trigger.addEventListener('click', (ev) =>
        {
            ev.preventDefault();

            const targetId = trigger.getAttribute('href');

            if (!targetId) {
                return;
            }

            Fancybox.show(
                [{ src: targetId, type: 'inline' }],
                {
                    mainClass: 'job-apply-modal',
                    dragToClose: false,
                    on: {
                        ready()
                        {
                            const wrapper = document.querySelector(targetId);
                            const form = wrapper?.querySelector('form.job-apply-form');

                            if (!wrapper || !form) {
                                return;
                            }

                            resetFormState(form, wrapper);
                            initBouncerForForm(form);
                            bindFormSubmission(form);
                        }
                    }
                }
            );
        });
    });
}
